import Column from '@amzn/meridian/column';
import { css, cx } from '@emotion/css';
import useSurfaceStyles from '../hooks/useSurfaceStyles';

const DEFAULT_SERVICE_CARD_SKELETON_COUNT = 6;
const TEXT_TO_LINE_HEIGHT_RATIO = 0.7;

interface SkeletonBlockProps {
  width: number | string;
  height: number;
}

export function SkeletonBlock({ width, height }: SkeletonBlockProps) {
  const { skeletonClass } = useSurfaceStyles();
  return <div className={cx(skeletonClass, css({ width, height }))} aria-hidden="true" />;
}

// Occupies exactly one line of text at the given line height so the real text replaces it without
// changing the layout height; the visible bar is shorter, like a glyph run inside its line box.
export function SkeletonLine({ width, lineHeight }: { width: number | string; lineHeight: number }) {
  const lineClass = css({ display: 'flex', alignItems: 'center', height: lineHeight });
  return (
    <div className={lineClass} aria-hidden="true">
      <SkeletonBlock width={width} height={Math.round(lineHeight * TEXT_TO_LINE_HEIGHT_RATIO)} />
    </div>
  );
}

// Mirrors ServiceCard: 16px x 20px padding, name (20px line), namespace (20px), metadata (16px),
// 2px gaps, and a 20px API count tag with the chevron on the right.
const serviceCardSkeletonLayoutClass = css({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  alignItems: 'center',
  gap: 16,
  padding: '16px 20px',
});

const serviceCardSkeletonTrailingClass = css({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

export function ServiceCardSkeletonList({
  count = DEFAULT_SERVICE_CARD_SKELETON_COUNT,
  label,
}: {
  count?: number;
  label: string;
}) {
  const { skeletonSurfaceClass } = useSurfaceStyles();

  return (
    <Column spacing="300" width="100%" role="status" aria-label={label}>
      {Array.from({ length: count }, (_, position) => (
        <div key={position} className={cx(skeletonSurfaceClass, serviceCardSkeletonLayoutClass)}>
          <Column spacing="100">
            <SkeletonLine width="38%" lineHeight={20} />
            <SkeletonLine width="24%" lineHeight={20} />
            <SkeletonLine width="30%" lineHeight={16} />
          </Column>
          <div className={serviceCardSkeletonTrailingClass}>
            <SkeletonBlock width={52} height={20} />
            <SkeletonBlock width={16} height={16} />
          </div>
        </div>
      ))}
    </Column>
  );
}
